import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import Button from '@/components/UI/Button';

interface LandingPageProps {
  onAccept: () => void;
  formName?: string;
}

const LandingPage = ({ onAccept, formName }: LandingPageProps) => {
  const [accepted, setAccepted] = useState(false);

  const handleAccept = () => {
    if (accepted) {
      onAccept();
    }
  };

  return (
    <div className='min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
      <div className='sm:mx-auto sm:w-full sm:max-w-3xl'>
        <div className='bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10'>
          <div className='text-center mb-8'>
            <h2 className='text-3xl font-extrabold text-gray-900 mb-4'>Welcome to {formName || 'Household Income Verification'}</h2>
            <p className='text-lg text-gray-600'>
              Please read the terms and conditions below before proceeding with your household income verification checklist.
            </p>
          </div>

          <div className='bg-gray-50 p-6 rounded-lg mb-8 max-h-96 overflow-y-auto'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>Terms and Conditions</h3>

            <div className='text-sm text-gray-700 space-y-4'>
              <p>
                <strong>1. Documentation Requirements</strong>
                <br />
                You will be asked to provide various documents including W-2 forms, IRS Form 1040, pay stubs, bank statements, and other
                income verification documents. Please ensure you have these readily available.
              </p>

              <p>
                <strong>2. Accuracy of Information</strong>
                <br />
                All information provided must be accurate and complete. Any false or misleading information may result in the rejection of
                your application or legal consequences. You are responsible for ensuring all documents are current and valid.
              </p>

              <p>
                <strong>3. Privacy and Data Protection</strong>
                <br />
                Your personal and financial information will be collected, used, and protected in accordance with applicable privacy laws.
                This sensitive information will be used solely for verification purposes and will not be shared with unauthorized third
                parties.
              </p>

              <p>
                <strong>4. Document Security</strong>
                <br />
                Please ensure that any documents you provide contain only necessary information. You may redact social security numbers or
                other sensitive data not required for income verification. Keep copies of all documents for your records.
              </p>

              <p>
                <strong>5. Processing Time</strong>
                <br />
                The verification process may take several business days to complete. You will be notified of the status of your verification
                via the contact information provided in this form.
              </p>

              <p>
                <strong>6. Additional Documentation</strong>
                <br />
                Additional documentation may be requested if the initial submission is incomplete or if clarification is needed. Failure to
                provide requested additional documentation may delay or prevent the completion of your verification.
              </p>

              <p>
                <strong>7. Technical Support</strong>
                <br />
                If you experience any technical difficulties while completing this form, please contact our support team. Save your progress
                frequently to avoid losing entered information.
              </p>
            </div>
          </div>

          <div className='flex items-start mb-6'>
            <div className='flex items-center h-5'>
              <input
                id='terms-acceptance'
                name='terms-acceptance'
                type='checkbox'
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className='focus:ring-primaryColor-normal h-4 w-4 text-primaryColor-normal border-gray-300 rounded'
              />
            </div>
            <div className='ml-3 text-sm'>
              <label htmlFor='terms-acceptance' className='font-medium text-gray-700'>
                I have read and agree to the terms and conditions
              </label>
              <p className='text-gray-500'>You must accept the terms and conditions to proceed with your household income verification.</p>
            </div>
          </div>

          <div className='flex justify-center'>
            <Button label='Continue to Verification' btnStyle='primary' type='button' disabled={!accepted} onClick={handleAccept} />
          </div>

          {accepted && (
            <div className='mt-4 flex items-center justify-center text-green-600'>
              <CheckCircleIcon className='h-5 w-5 mr-2' />
              <span className='text-sm font-medium'>Terms accepted - Ready to proceed</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
